import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Card,
    Badge,
    Field,
    Input,
    Select,
    Tab,
    TabList,
    Divider,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    Toast,
    ToastTitle,
    Toaster,
    useToastController,
} from '@fluentui/react-components';
import {
    Money24Regular,
    DocumentText24Regular,
    Send24Regular,
    Edit24Regular,
    Warning24Regular,
    CheckmarkCircle24Regular,
    Dismiss24Regular,
    Info24Regular,
    Calculator24Regular,
} from '@fluentui/react-icons';

interface CPTCode {
    code: string;
    description: string;
    baseRate: number;
    type?: 'primary' | 'additional_service' | 'modifier';
    rateAdjustment?: number;
}

interface DiagnosisCode {
    code: string;
    description: string;
    primary: boolean;
}

interface BillingData {
    consultationId: string;
    patientId: string;
    providerId: string;
    serviceDate: string;
    primaryCPT: CPTCode;
    additionalCPTs: CPTCode[];
    diagnosisCodes: DiagnosisCode[];
    chargeAmount: number;
    allowedAmount: number;
    patientResponsibility: number;
    insuranceAmount: number;
    duration: number;
    modifiers: string[];
    status: string;
    metadata: {
        consultationType: string;
        priority: string;
        interpreterUsed: boolean;
        language: string;
        insuranceType: string;
        confidence: number;
    };
}

interface Recommendation {
    type: 'warning' | 'review' | 'approval' | 'info';
    message: string;
}

interface BillingReviewInterfaceProps {
    isOpen: boolean;
    consultationId: string;
    patientName: string;
    billingData: BillingData;
    recommendations: Recommendation[];
    onApprove: (approvedBillingData: BillingData) => void;
    onReject: (reason: string) => void;
    onEdit: (editedBillingData: BillingData) => void;
    onCancel: () => void;
    loading?: boolean;
}

export const BillingReviewInterface: React.FC<BillingReviewInterfaceProps> = ({
    isOpen,
    consultationId,
    patientName,
    billingData,
    recommendations,
    onApprove,
    onReject,
    onEdit,
    onCancel,
    loading = false,
}) => {
    const [activeTab, setActiveTab] = useState<string>('summary');
    const [editedBilling, setEditedBilling] = useState<BillingData>(billingData);
    const [hasChanges, setHasChanges] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const { dispatchToast } = useToastController();

    useEffect(() => {
        setEditedBilling(billingData);
        setHasChanges(false);
    }, [billingData]);

    const handleFieldChange = (field: string, value: any) => {
        setEditedBilling((prev) => ({
            ...prev,
            [field]: value,
        }));
        setHasChanges(true);
    };

    const handleCPTChange = (newCode: string, newDescription: string, newRate: number) => {
        setEditedBilling((prev) => ({
            ...prev,
            primaryCPT: {
                ...prev.primaryCPT,
                code: newCode,
                description: newDescription,
                baseRate: newRate,
            },
        }));
        setHasChanges(true);
        recalculateAmounts();
    };

    const recalculateAmounts = () => {
        // Simplified recalculation - in real app would use billing service
        const baseAmount = editedBilling.primaryCPT.baseRate;
        const additionalAmount = editedBilling.additionalCPTs.reduce(
            (sum, cpt) => sum + (cpt.baseRate || 0),
            0
        );
        const totalCharge = baseAmount + additionalAmount;

        setEditedBilling((prev) => ({
            ...prev,
            chargeAmount: totalCharge,
            allowedAmount: totalCharge * 0.85, // Simplified insurance adjustment
            patientResponsibility: Math.min(50, totalCharge * 0.2), // Simplified patient responsibility
            insuranceAmount: totalCharge * 0.8 - Math.min(50, totalCharge * 0.2),
        }));
    };

    const handleApprove = () => {
        onApprove(editedBilling);
        dispatchToast(
            <Toast>
                <ToastTitle>Billing Approved</ToastTitle>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleReject = () => {
        if (!rejectReason.trim()) {
            dispatchToast(
                <Toast>
                    <ToastTitle>Please provide a reason for rejection</ToastTitle>
                </Toast>,
                { intent: 'warning' }
            );
            return;
        }
        onReject(rejectReason);
        setShowRejectDialog(false);
    };

    const handleSaveChanges = () => {
        onEdit(editedBilling);
        setHasChanges(false);
        dispatchToast(
            <Toast>
                <ToastTitle>Changes Saved</ToastTitle>
            </Toast>,
            { intent: 'success' }
        );
    };

    const getRecommendationIcon = (type: string) => {
        switch (type) {
            case 'warning':
                return <Warning24Regular style={{ color: '#ffa500' }} />;
            case 'review':
                return <Info24Regular style={{ color: '#0078d4' }} />;
            case 'approval':
                return <CheckmarkCircle24Regular style={{ color: '#107c10' }} />;
            default:
                return <Info24Regular style={{ color: '#605e5c' }} />;
        }
    };

    const getRecommendationBadge = (type: string) => {
        switch (type) {
            case 'warning':
                return (
                    <Badge appearance="filled" color="danger">
                        Warning
                    </Badge>
                );
            case 'review':
                return (
                    <Badge appearance="tint" color="brand">
                        Review
                    </Badge>
                );
            case 'approval':
                return (
                    <Badge appearance="outline" color="success">
                        Approval Required
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Info</Badge>;
        }
    };

    const getConfidenceBadge = (confidence: number) => {
        if (confidence >= 90) {
            return (
                <Badge appearance="filled" color="success">
                    High Confidence
                </Badge>
            );
        } else if (confidence >= 80) {
            return (
                <Badge appearance="tint" color="warning">
                    Medium Confidence
                </Badge>
            );
        } else {
            return (
                <Badge appearance="filled" color="danger">
                    Low Confidence
                </Badge>
            );
        }
    };

    const renderSummaryTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Billing Overview */}
            <Card style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}
                >
                    <Money24Regular style={{ fontSize: '24px', color: '#0078d4' }} />
                    <Text size={600} weight="semibold">
                        Billing Summary
                    </Text>
                    {getConfidenceBadge(editedBilling.metadata.confidence)}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Total Charge
                        </Text>
                        <Text
                            size={700}
                            weight="bold"
                            style={{ display: 'block', color: '#0078d4' }}
                        >
                            ${editedBilling.chargeAmount.toFixed(2)}
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Patient Responsibility
                        </Text>
                        <Text
                            size={700}
                            weight="bold"
                            style={{ display: 'block', color: '#d13438' }}
                        >
                            ${editedBilling.patientResponsibility.toFixed(2)}
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Insurance Amount
                        </Text>
                        <Text
                            size={700}
                            weight="bold"
                            style={{ display: 'block', color: '#107c10' }}
                        >
                            ${editedBilling.insuranceAmount.toFixed(2)}
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Allowed Amount
                        </Text>
                        <Text size={500} weight="semibold" style={{ display: 'block' }}>
                            ${editedBilling.allowedAmount.toFixed(2)}
                        </Text>
                    </div>
                </div>
            </Card>

            {/* Service Details */}
            <Card style={{ padding: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    Service Details
                </Text>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Primary CPT Code
                        </Text>
                        <Text size={400} weight="semibold" style={{ display: 'block' }}>
                            {editedBilling.primaryCPT.code} - {editedBilling.primaryCPT.description}
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Duration
                        </Text>
                        <Text size={400} style={{ display: 'block' }}>
                            {editedBilling.duration} minutes
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Service Date
                        </Text>
                        <Text size={400} style={{ display: 'block' }}>
                            {new Date(editedBilling.serviceDate).toLocaleDateString()}
                        </Text>
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Modifiers
                        </Text>
                        <Text size={400} style={{ display: 'block' }}>
                            {editedBilling.modifiers.join(', ')}
                        </Text>
                    </div>
                </div>
            </Card>

            {/* Recommendations */}
            {recommendations.length > 0 && (
                <Card style={{ padding: '20px' }}>
                    <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                        Billing Recommendations
                    </Text>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {recommendations.map((rec, index) => (
                            <div
                                key={index}
                                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                            >
                                {getRecommendationIcon(rec.type)}
                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {getRecommendationBadge(rec.type)}
                                        <Text size={400}>{rec.message}</Text>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );

    const renderCodesTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* CPT Codes */}
            <Card style={{ padding: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    CPT Codes
                </Text>

                <DataGrid
                    items={[
                        { ...editedBilling.primaryCPT, type: 'Primary', isPrimary: true },
                        ...editedBilling.additionalCPTs.map((cpt: any) => ({
                            ...cpt,
                            type: cpt.type || 'Additional',
                            isPrimary: false,
                        })),
                    ]}
                    columns={[
                        {
                            columnId: 'code',
                            renderHeaderCell: () => 'Code',
                            renderCell: (item: any) => item.code,
                            compare: (a: any, b: any) => a.code.localeCompare(b.code),
                        },
                        {
                            columnId: 'description',
                            renderHeaderCell: () => 'Description',
                            renderCell: (item: any) => item.description,
                            compare: (a: any, b: any) => a.description.localeCompare(b.description),
                        },
                        {
                            columnId: 'rate',
                            renderHeaderCell: () => 'Rate',
                            renderCell: (item: any) => `$${(item.baseRate || 0).toFixed(2)}`,
                            compare: (a: any, b: any) => (a.baseRate || 0) - (b.baseRate || 0),
                        },
                        {
                            columnId: 'type',
                            renderHeaderCell: () => 'Type',
                            renderCell: (item: any) =>
                                item.isPrimary ? (
                                    <Badge appearance="filled" color="brand">
                                        Primary
                                    </Badge>
                                ) : (
                                    <Badge appearance="outline">{item.type}</Badge>
                                ),
                            compare: (a: any, b: any) => a.type.localeCompare(b.type),
                        },
                    ]}
                />
            </Card>

            {/* Diagnosis Codes */}
            <Card style={{ padding: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    Diagnosis Codes (ICD-10)
                </Text>

                <DataGrid
                    items={editedBilling.diagnosisCodes}
                    columns={[
                        {
                            columnId: 'code',
                            renderHeaderCell: () => 'Code',
                            renderCell: (item: any) => item.code,
                            compare: (a: any, b: any) => a.code.localeCompare(b.code),
                        },
                        {
                            columnId: 'description',
                            renderHeaderCell: () => 'Description',
                            renderCell: (item: any) => item.description,
                            compare: (a: any, b: any) => a.description.localeCompare(b.description),
                        },
                        {
                            columnId: 'primary',
                            renderHeaderCell: () => 'Primary',
                            renderCell: (item: any) =>
                                item.primary ? (
                                    <Badge appearance="filled" color="success">
                                        Primary
                                    </Badge>
                                ) : (
                                    <Badge appearance="outline">Secondary</Badge>
                                ),
                            compare: (a: any, b: any) => (a.primary ? 1 : 0) - (b.primary ? 1 : 0),
                        },
                    ]}
                />
            </Card>
        </div>
    );

    const renderEditTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Primary CPT Code Editing */}
            <Card style={{ padding: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    Edit Primary CPT Code
                </Text>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Field label="CPT Code">
                        <Input
                            value={editedBilling.primaryCPT.code}
                            onChange={(_, data) =>
                                handleCPTChange(
                                    data.value,
                                    editedBilling.primaryCPT.description,
                                    editedBilling.primaryCPT.baseRate
                                )
                            }
                        />
                    </Field>
                    <Field label="Base Rate">
                        <Input
                            type="number"
                            value={editedBilling.primaryCPT.baseRate.toString()}
                            onChange={(_, data) =>
                                handleCPTChange(
                                    editedBilling.primaryCPT.code,
                                    editedBilling.primaryCPT.description,
                                    parseFloat(data.value) || 0
                                )
                            }
                        />
                    </Field>
                </div>

                <Field label="Description" style={{ marginTop: '16px' }}>
                    <Input
                        value={editedBilling.primaryCPT.description}
                        onChange={(_, data) =>
                            handleCPTChange(
                                editedBilling.primaryCPT.code,
                                data.value,
                                editedBilling.primaryCPT.baseRate
                            )
                        }
                    />
                </Field>
            </Card>

            {/* Amounts */}
            <Card style={{ padding: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    Billing Amounts
                </Text>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Field label="Total Charge">
                        <Input
                            type="number"
                            value={editedBilling.chargeAmount.toString()}
                            onChange={(_, data) =>
                                handleFieldChange('chargeAmount', parseFloat(data.value) || 0)
                            }
                        />
                    </Field>
                    <Field label="Patient Responsibility">
                        <Input
                            type="number"
                            value={editedBilling.patientResponsibility.toString()}
                            onChange={(_, data) =>
                                handleFieldChange(
                                    'patientResponsibility',
                                    parseFloat(data.value) || 0
                                )
                            }
                        />
                    </Field>
                </div>

                <Button
                    appearance="outline"
                    icon={<Calculator24Regular />}
                    onClick={recalculateAmounts}
                    style={{ marginTop: '16px' }}
                >
                    Recalculate Amounts
                </Button>
            </Card>
        </div>
    );

    return (
        <>
            <Dialog open={isOpen}>
                <DialogSurface style={{ maxWidth: '900px', width: '95vw', height: '90vh' }}>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                    onClick={onCancel}
                                />
                            }
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <DocumentText24Regular
                                    style={{ fontSize: '24px', color: '#0078d4' }}
                                />
                                Billing Review - {patientName}
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div style={{ marginBottom: '16px' }}>
                                <Text size={400} style={{ color: '#605e5c' }}>
                                    Consultation ID: {consultationId} |{' '}
                                    {editedBilling.metadata.consultationType}
                                </Text>
                            </div>

                            <TabList
                                selectedValue={activeTab}
                                onTabSelect={(_, data) => setActiveTab(data.value as string)}
                                style={{ marginBottom: '20px' }}
                            >
                                <Tab value="summary" icon={<Money24Regular />}>
                                    Summary
                                </Tab>
                                <Tab value="codes" icon={<DocumentText24Regular />}>
                                    Codes & Diagnosis
                                </Tab>
                                <Tab value="edit" icon={<Edit24Regular />}>
                                    Edit Billing
                                    {hasChanges && (
                                        <Badge
                                            size="small"
                                            color="warning"
                                            style={{ marginLeft: '8px' }}
                                        >
                                            *
                                        </Badge>
                                    )}
                                </Tab>
                            </TabList>

                            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                {activeTab === 'summary' && renderSummaryTab()}
                                {activeTab === 'codes' && renderCodesTab()}
                                {activeTab === 'edit' && renderEditTab()}
                            </div>
                        </DialogContent>

                        <DialogActions>
                            <Button appearance="secondary" onClick={onCancel}>
                                Cancel
                            </Button>

                            <Button
                                appearance="outline"
                                onClick={() => setShowRejectDialog(true)}
                                disabled={loading}
                            >
                                Reject
                            </Button>

                            {hasChanges && (
                                <Button
                                    appearance="outline"
                                    icon={<Edit24Regular />}
                                    onClick={handleSaveChanges}
                                    disabled={loading}
                                >
                                    Save Changes
                                </Button>
                            )}

                            <Button
                                appearance="primary"
                                icon={<Send24Regular />}
                                onClick={handleApprove}
                                disabled={loading}
                            >
                                {loading ? 'Submitting...' : 'Approve & Submit'}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>

                <Toaster />
            </Dialog>

            {/* Reject Dialog - Outside main dialog */}
            {showRejectDialog && (
                <Dialog open={showRejectDialog}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Reject Billing</DialogTitle>
                            <DialogContent>
                                <Field label="Reason for rejection" required>
                                    <Input
                                        value={rejectReason}
                                        onChange={(_, data) => setRejectReason(data.value)}
                                        placeholder="Please provide a reason..."
                                    />
                                </Field>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    appearance="secondary"
                                    onClick={() => setShowRejectDialog(false)}
                                >
                                    Cancel
                                </Button>
                                <Button appearance="primary" onClick={handleReject}>
                                    Reject
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </>
    );
};
